import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { ENV } from '../../../config/env';
import toast from 'react-hot-toast';

interface CategoryContent {
  h1: string;
  metaTitle: string;
  metaDescription: string;
  introText: string;
  seoText: string;
  filterGuidance: string;
  internalLinks: string[];
}

const CategoryPageTab: React.FC = () => {
  const { user, getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [subcategories, setSubcategories] = useState('');
  const [targetAudience, setTargetAudience] = useState('General shoppers');
  const [keywords, setKeywords] = useState('');
  const [content, setContent] = useState<CategoryContent | null>(null);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!categoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    try {
      setLoading(true);
      toast.loading('Generating category page content...');

      const userId = await getToken();
      const response = await fetch(`${ENV.API_URL}/api/marketing/category-page`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          categoryName,
          subcategories: subcategories.split(',').map(s => s.trim()).filter(s => s),
          targetAudience,
          keywords: keywords.split(',').map(k => k.trim()).filter(k => k)
        })
      });

      if (!response.ok) throw new Error('Failed to generate content');

      const data = await response.json();
      toast.dismiss();
      setContent(data.content);
      toast.success('Category page content generated!');
    } catch (error) {
      toast.dismiss();
      console.error('Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to generate content');
    } finally {
      setLoading(false);
    }
  };

  const copySection = (text: string, section: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${section} copied!`);
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Category Page Generator</h3>
        <p className="text-gray-600 text-sm">
          Generate SEO-optimized category page content including H1, meta tags, descriptions, and filtering guidance.
        </p>
      </div>

      {!content ? (
        <form onSubmit={handleGenerate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category Name
            </label>
            <input
              type="text"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="e.g., Women's Running Shoes"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subcategories (comma-separated)
            </label>
            <input
              type="text"
              value={subcategories}
              onChange={(e) => setSubcategories(e.target.value)}
              placeholder="e.g., Trail Running, Road Running, Track Shoes"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Audience
            </label>
            <select
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="General shoppers">General shoppers</option>
              <option value="Budget-conscious buyers">Budget-conscious buyers</option>
              <option value="Premium customers">Premium customers</option>
              <option value="Professionals">Professionals</option>
              <option value="Enthusiasts">Enthusiasts</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SEO Keywords (optional)
            </label>
            <input
              type="text"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="e.g., running shoes, athletic footwear, sports shoes"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`px-6 py-3 rounded-lg font-medium text-white transition ${
              loading ? 'bg-orange-400 cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-700'
            }`}
          >
            {loading ? 'Generating...' : 'Generate Category Page'}
          </button>
        </form>
      ) : (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h4 className="text-lg font-semibold text-gray-900">Category Page Content</h4>
            <button
              onClick={() => setContent(null)}
              className="text-sm text-orange-600 hover:text-orange-700 font-medium"
            >
              Generate New
            </button>
          </div>

          {/* H1 Heading */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <h5 className="font-semibold text-gray-900">H1 Heading</h5>
              <button
                onClick={() => copySection(content.h1, 'H1')}
                className="text-sm text-orange-600 hover:text-orange-700 font-medium"
              >
                Copy
              </button>
            </div>
            <p className="text-2xl font-bold text-gray-900">{content.h1}</p>
          </div>

          {/* Meta Tags */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <h5 className="font-semibold text-gray-900 mb-3">Meta Tags</h5>
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">Meta Title</span>
                  <button
                    onClick={() => copySection(content.metaTitle, 'Meta title')}
                    className="text-xs text-orange-600 hover:text-orange-700"
                  >
                    Copy
                  </button>
                </div>
                <p className="text-gray-900">{content.metaTitle}</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">Meta Description</span>
                  <button
                    onClick={() => copySection(content.metaDescription, 'Meta description')}
                    className="text-xs text-orange-600 hover:text-orange-700"
                  >
                    Copy
                  </button>
                </div>
                <p className="text-gray-700">{content.metaDescription}</p>
              </div>
            </div>
          </div>

          {/* Intro Text */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <h5 className="font-semibold text-gray-900">Introduction Text</h5>
              <button
                onClick={() => copySection(content.introText, 'Intro text')}
                className="text-sm text-orange-600 hover:text-orange-700 font-medium"
              >
                Copy
              </button>
            </div>
            <p className="text-gray-700 whitespace-pre-wrap">{content.introText}</p>
          </div>

          {/* SEO Text */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <h5 className="font-semibold text-gray-900">SEO Text (Bottom of Page)</h5>
              <button
                onClick={() => copySection(content.seoText, 'SEO text')}
                className="text-sm text-orange-600 hover:text-orange-700 font-medium"
              >
                Copy
              </button>
            </div>
            <p className="text-gray-700 whitespace-pre-wrap">{content.seoText}</p>
          </div>

          {/* Filter Guidance */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <h5 className="font-semibold text-gray-900">Product Filter Guidance</h5>
              <button
                onClick={() => copySection(content.filterGuidance, 'Filter guidance')}
                className="text-sm text-orange-600 hover:text-orange-700 font-medium"
              >
                Copy
              </button>
            </div>
            <p className="text-gray-700 whitespace-pre-wrap">{content.filterGuidance}</p>
          </div>

          {/* Internal Links */}
          {content.internalLinks.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <h5 className="font-semibold text-gray-900 mb-3">Suggested Internal Links</h5>
              <ul className="space-y-2">
                {content.internalLinks.map((link, i) => (
                  <li key={i} className="text-gray-700 flex items-start">
                    <span className="text-orange-600 mr-2">→</span>
                    {link}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CategoryPageTab;
